import { IcoArrowDown } from "assets/icons";
import { Label } from "components";
import Select, { components, type DropdownIndicatorProps } from "react-select";
import "./styles.scss";

// eslint-disable-next-line react/function-component-definition
const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
	return (
		<components.DropdownIndicator {...props}>
			<IcoArrowDown color="#1d1d1d" />
		</components.DropdownIndicator>
	);
};

export interface OptionProps {
	value: string;
	label: React.ReactNode;
}

export interface SelectFormProps {
	disabled?: boolean;
	isClearable?: boolean;
	isRtl?: boolean;
	name: string;
	defaultValue?: any;
	label?: string;
	isLoading?: boolean;
	options?: OptionProps[];
	placeholder?: string;
	noOptionsMessage?: string;
	value: any;
	setFieldValue?: (field: string, value: any) => void;
	onChange?: (e: any) => void;
	onBlur?: (e: any) => void;
	getOptionValue?: (option: OptionProps) => any;
	required?: boolean;
	touched?: boolean;
	error?: string[] | string;
	className?: string;
	hideOptional?: boolean;
	onMenuScrollToBottom?: () => void;
	onMenuClose?: () => void;
	InputComponent?: (innerInputProps: any) => JSX.Element;
}

const stylesSelect = () => {
	return {
		control: (baseStyles: any) => ({
			...baseStyles,
			borderColor: "#DBDBDB",
			boxShadow: "none",
			"&:active": {
				boxSizing: "border-box",
				borderColor: "#333333",
				borderWidth: "0.125rem"
			},
			padding: "12px 16px"
		}),
		dropdownIndicator: (provided: any, state: any) => ({
			...provided,
			transform: state.selectProps.menuIsOpen && "rotate(180deg)"
		}),
		menuList: (baseStyles: any) => ({
			...baseStyles,
			maxHeight: "240px"
		})
	};
};

const themeSelect = () => {
	return (theme: any) => ({
		...theme,
		borderRadius: 4,
		colors: {
			...theme.colors,
			primary25: "#DBDBDB",
			primary50: "#F0F0F0",
			primary: "black"
		}
	});
};

function getOptionWithValue(
	options: any[],
	value: any,
	getOptVal?: (option: any) => any
) {
	if (value === undefined || value === null || !getOptVal) return value;
	const optionValue = options?.find((o) => getOptVal(o) === value);
	return optionValue;
}

export default function SelectForm({
	disabled = false,
	isClearable = true,
	isRtl = false,
	name,
	defaultValue = null,
	label = "",
	isLoading,
	options,
	placeholder,
	noOptionsMessage,
	value,
	setFieldValue,
	onChange,
	onBlur,
	getOptionValue,
	className = "",
	required,
	error,
	touched,
	hideOptional,
	onMenuScrollToBottom,
	onMenuClose,
	InputComponent
}: Readonly<SelectFormProps>): JSX.Element {
	if (!options) return <div />;

	const onSelectChange = (newFieldValue: any) => {
		onChange?.(newFieldValue);
		const newValue =
			newFieldValue && getOptionValue
				? getOptionValue(newFieldValue)
				: newFieldValue;
		setFieldValue?.(name, newValue);
	};

	return (
		<div className={`container ${className}`}>
			<Label isRequired={!!required} hideOptional={hideOptional}>
				{label}
			</Label>
			<Select
				className={`basic-single font-light ${error && touched ? "error" : ""}`}
				classNamePrefix="select"
				name={name}
				menuPlacement="auto"
				minMenuHeight={200}
				defaultValue={defaultValue}
				isDisabled={disabled}
				isLoading={isLoading}
				isClearable={isClearable}
				isRtl={isRtl}
				value={getOptionWithValue(options, value, getOptionValue)}
				isSearchable
				options={options}
				onChange={onSelectChange}
				onBlur={onBlur}
				components={{
					DropdownIndicator,
					IndicatorSeparator: () => null,
					Input: InputComponent ?? components.Input
				}}
				styles={stylesSelect()}
				theme={themeSelect()}
				placeholder={placeholder}
				noOptionsMessage={() => noOptionsMessage}
				onMenuScrollToBottom={onMenuScrollToBottom}
				onMenuClose={onMenuClose}
			/>
			{error && touched && (
				<div className="white-space--pre-line mt-1 my-0 error-text">
					{Array.isArray(error) ? error.join("\n") : error}
				</div>
			)}
		</div>
	);
}
